// Topic management functions
// Topics are stored in the admin collection as a subcollection
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  where,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore'
import { db } from './config'

// Topics are stored as a subcollection under admin/settings
// Path structure: admin/settings/topics (3 segments = valid collection path)
const ADMIN_SETTINGS_DOC = 'admin/settings'
const TOPICS_COLLECTION = 'topics'

/**
 * Create a new topic
 * @param {string} topicText - Topic text (sentence)
 * @param {string} categoryId - Optional product ID to assign as category
 * @returns {Promise<string>} Document ID
 */
export const createTopic = async (topicText, categoryId = null) => {
  try {
    // Normalize topic text: trim leading/trailing whitespace and convert to lowercase
    const trimmedText = topicText.trim()
    const normalizedText = trimmedText.toLowerCase()
    
    // Check for duplicates (case-insensitive, ignoring leading/trailing whitespace)
    // A duplicate exists only if both text AND category match
    const adminSettingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const topicsRef = collection(adminSettingsRef, TOPICS_COLLECTION)
    const existingTopicsQuery = query(topicsRef)
    const existingTopicsSnapshot = await getDocs(existingTopicsQuery)
    
    // Check if topic already exists with same text AND same category
    const duplicate = existingTopicsSnapshot.docs.find(doc => {
      const data = doc.data()
      if (!data.text) return false
      const existingNormalized = data.text.trim().toLowerCase()
      const textMatches = existingNormalized === normalizedText
      
      // Get existing category (null if not set)
      const existingCategory = data.category || null
      const categoryMatches = existingCategory === categoryId
      
      // Duplicate only if both text and category match
      return textMatches && categoryMatches
    })
    
    if (duplicate) {
      throw new Error('A topic with this text and category already exists')
    }
    
    // Normalize category: convert empty string to null
    const normalizedCategory = (categoryId && categoryId.trim()) ? categoryId.trim() : null
    
    const topicData = {
      text: trimmedText,
      status: 'unprocessed',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      category: normalizedCategory
    }
    
    const docRef = await addDoc(topicsRef, topicData)
    return docRef.id
  } catch (error) {
    console.error('Error creating topic:', error)
    throw error
  }
}

/**
 * Update an existing topic
 * @param {string} topicId - Topic document ID
 * @param {Object} updates - Fields to update
 */
export const updateTopic = async (topicId, updates) => {
  try {
    const adminSettingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const topicsRef = collection(adminSettingsRef, TOPICS_COLLECTION)
    const docRef = doc(topicsRef, topicId)
    
    // Get current topic data to check for duplicates
    const currentTopicSnap = await getDoc(docRef)
    const currentTopicData = currentTopicSnap.exists() ? currentTopicSnap.data() : {}
    
    // Determine the text and category we're checking against
    const newText = updates.text !== undefined ? updates.text.trim() : currentTopicData.text
    const newCategory = updates.category !== undefined 
      ? (updates.category === null || updates.category === '' ? null : updates.category)
      : (currentTopicData.category || null)
    
    // Normalize text for comparison
    const normalizedText = newText ? newText.toLowerCase() : ''
    
    // Check for duplicates if text or category is being updated
    if (updates.text !== undefined || updates.category !== undefined) {
      const existingTopicsQuery = query(topicsRef)
      const existingTopicsSnapshot = await getDocs(existingTopicsQuery)
      
      // Check if topic already exists with same text AND same category (excluding current topic)
      const duplicate = existingTopicsSnapshot.docs.find(doc => {
        if (doc.id === topicId) return false // Skip current topic
        const data = doc.data()
        if (!data.text) return false
        const existingNormalized = data.text.trim().toLowerCase()
        const textMatches = existingNormalized === normalizedText
        
        // Get existing category (null if not set)
        const existingCategory = data.category || null
        const categoryMatches = existingCategory === newCategory
        
        // Duplicate only if both text and category match
        return textMatches && categoryMatches
      })
      
      if (duplicate) {
        throw new Error('A topic with this text and category already exists')
      }
    }
    
    // Prepare update data
    const updateData = {
      ...updates,
      updatedAt: serverTimestamp()
    }
    
    // Handle text update
    if (updates.text !== undefined) {
      updateData.text = updates.text.trim()
    }
    
    // Handle category: if explicitly set to null/empty string, remove it
    if (updates.category !== undefined) {
      if (updates.category === null || updates.category === '') {
        updateData.category = null
      } else {
        updateData.category = updates.category
      }
    }
    
    await updateDoc(docRef, updateData)
  } catch (error) {
    console.error('Error updating topic:', error)
    throw error
  }
}

/**
 * Delete a topic
 * @param {string} topicId - Topic document ID
 */
export const deleteTopic = async (topicId) => {
  try {
    const adminSettingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const topicsRef = collection(adminSettingsRef, TOPICS_COLLECTION)
    const docRef = doc(topicsRef, topicId)
    await deleteDoc(docRef)
  } catch (error) {
    console.error('Error deleting topic:', error)
    throw error
  }
}

/**
 * Get a single topic by ID
 * @param {string} topicId - Topic document ID
 * @returns {Promise<Object|null>} Topic data
 */
export const getTopic = async (topicId) => {
  try {
    const adminSettingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const topicsRef = collection(adminSettingsRef, TOPICS_COLLECTION)
    const docRef = doc(topicsRef, topicId)
    const docSnap = await getDoc(docRef)
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() }
    }
    return null
  } catch (error) {
    console.error('Error getting topic:', error)
    throw error
  }
}

/**
 * Get all topics
 * @param {boolean} includeUsed - Whether to include used topics
 * @returns {Promise<Array>} Array of topics
 */
export const getTopics = async (includeUsed = true) => {
  try {
    const adminSettingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const topicsRef = collection(adminSettingsRef, TOPICS_COLLECTION)
    
    let q
    if (includeUsed) {
      q = query(topicsRef, orderBy('createdAt', 'desc'))
    } else {
      q = query(
        topicsRef,
        where('status', 'in', ['unprocessed', 'available']),
        orderBy('createdAt', 'desc')
      )
    }
    
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  } catch (error) {
    console.error('Error getting topics:', error)
    throw error
  }
}

/**
 * Subscribe to topics changes (real-time)
 * @param {Function} callback - Callback function
 * @param {boolean} includeUsed - Whether to include used topics
 * @returns {Function} Unsubscribe function
 */
export const subscribeToTopics = (callback, includeUsed = true) => {
  const adminSettingsRef = doc(db, ADMIN_SETTINGS_DOC)
  const topicsRef = collection(adminSettingsRef, TOPICS_COLLECTION)
  
  let q
  if (includeUsed) {
    q = query(topicsRef, orderBy('createdAt', 'desc'))
  } else {
    q = query(
      topicsRef,
      where('status', 'in', ['unprocessed', 'available']),
      orderBy('createdAt', 'desc')
    )
  }
  
  return onSnapshot(q, (snapshot) => {
    const topics = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
    callback(topics)
  }, (error) => {
    console.error('Error subscribing to topics:', error)
  })
}

/**
 * Mark a topic as used
 * @param {string} topicId - Topic document ID
 */
export const markTopicAsUsed = async (topicId) => {
  try {
    await updateTopic(topicId, { status: 'used' })
  } catch (error) {
    console.error('Error marking topic as used:', error)
    throw error
  }
}

/**
 * Mark a topic as available (admin only)
 * @param {string} topicId - Topic document ID
 */
export const markTopicAsUnused = async (topicId) => {
  try {
    await updateTopic(topicId, { status: 'available' })
  } catch (error) {
    console.error('Error marking topic as available:', error)
    throw error
  }
}
